import { links } from "../lib/gate";

/** The landing sections after the hero (K-1). Copy from design/KGOB-Events.dc.html; every action goes through the gate. */
const benefits = [
  { title: "Build valuable connections", body: "Meet other owners, advisors and neighbours in a relaxed setting. Real conversations, lasting relationships.", hue: 50 },
  { title: "Gain practical insights", body: "Timely tax, accounting and planning guidance from KGOB partners and guest advisors, with time for your questions.", hue: 80 },
  { title: "Protect what matters most", body: "Strategies to build, protect and preserve the business, family and legacy you have worked so hard to create.", hue: 200 },
];

const steps = [
  ["Pick an event and a date", "Filter by state, city or online. Every date shows its availability."],
  ["Tell us who is coming", "Your name, business and phone. Add up to three guests by name."],
  ["Enter the 6-digit code we email you", "That is your sign-in. No password, no account to create."],
  ["You are in", "A confirmation and calendar invite arrive by email. Change dates, guests or cancel any time before the cutoff."],
];

const faqs = [
  { q: "Can I bring a guest?", a: "Most events allow up to three named guests per RSVP; the event page shows the limit. Each guest takes a seat, and you can add or remove guests later from My RSVPs." },
  { q: "How do I change or cancel?", a: "Sign in with your email and a 6-digit code, then change the date, edit guests or cancel. Date and guest changes close 24 hours before the start; cancelling works until the event begins.", link: { href: links.my, text: "Manage my RSVPs →" } },
  { q: "Will I get a calendar invite?", a: "Yes. A confirmation email with a calendar invite arrives as soon as your seat is confirmed, and it updates automatically if anything changes. Online events include the join link." },
  { q: "Is there parking?", a: "Every in-person event page lists the venue address, a map link and access notes: parking, entrance and accessibility. The same details are in your confirmation email.", link: { href: "#ev-list", text: "See upcoming venues →" } },
];

const typeVars = (hue) => ({ "--type": `oklch(0.52 0.13 ${hue})`, "--type-tint": `oklch(0.52 0.13 ${hue} / 0.14)` });

export function WhyCome() {
  return (
    <section className="ev-wrap ev-section" aria-labelledby="ev-why-h">
      <p className="ev-eyebrow">Why come</p>
      <h2 id="ev-why-h" style={{ maxWidth: "20ch" }}>Connect. Learn. Grow.</h2>
      <div className="ev-benefits">
        {benefits.map((b, i) => (
          <article key={b.title} className="ev-card ev-benefit ev-up" style={typeVars(b.hue)}>
            <span className="ev-num" aria-hidden="true">{i + 1}</span>
            <h3>{b.title}</h3>
            <p>{b.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section className="ev-band" aria-labelledby="ev-how-h">
      <div className="ev-wrap ev-section ev-how">
        <div className="ev-intro">
          <p className="ev-eyebrow">How it works</p>
          <h2 id="ev-how-h" style={{ marginBottom: 16 }}>From this page to a confirmed seat in <em>under a minute.</em></h2>
          <p>Everything after this page happens on our secure events site. No account or password: a 6-digit code emailed to you confirms it&rsquo;s really you.</p>
          <a className="ev-btn ev-btn-gold" href="#ev-list" data-testid="how-browse">Browse events <span aria-hidden="true">→</span></a>
        </div>
        <ol className="ev-steps">
          {steps.map(([t, d], i) => (
            <li key={t} className="ev-card">
              <span className="ev-num" aria-hidden="true">{i + 1}</span>
              <span><strong>{t}</strong><span className="ev-body">{d}</span></span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <section className="ev-wrap ev-section" aria-labelledby="ev-faq-h">
      <p className="ev-eyebrow">Good to know</p>
      <h2 id="ev-faq-h">Questions, answered</h2>
      <dl className="ev-faq">
        {faqs.map((f) => (
          <div key={f.q} className="ev-card">
            <dt>{f.q}</dt>
            <dd>{f.a} {f.link && <a href={f.link.href} data-testid={`faq-link-${f.q.slice(0, 18).toLowerCase().replace(/[^a-z]+/g, "-")}`}>{f.link.text}</a>}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function ContactBand({ email = "events@kgob.com", phone = "1-844-599-3355" }) {
  return (
    <section className="ev-wrap ev-contact-wrap" aria-labelledby="ev-contact-h">
      <div className="ev-contact">
        <div>
          <h2 id="ev-contact-h">Questions about an event?</h2>
          <p>Our events team answers within one business day.</p>
        </div>
        <div className="ev-ctas">
          <a className="ev-btn ev-btn-white" href={`mailto:${email}`} data-testid="contact-band-email">{email}</a>
          <a className="ev-btn ev-btn-ghost" href={`tel:${phone.replace(/\D/g, "")}`} data-testid="contact-band-phone">{phone}</a>
        </div>
      </div>
    </section>
  );
}
